import {
  Group,
  type GroupProps,
  InputLabel,
  type InputLabelProps,
  Text
} from "@mantine/core";
import { IconBetaFeature } from "../../../IconBetaFeature";
import { IconHelpText } from "../../../IconHelpText/IconHelpText";
import { OrangeCircle } from "../../../OrangeCircle/OrangeCircle";
import { type FormFieldProps } from "./FormField";

type FormFieldLabelProps = Pick<
  FormFieldProps,
  "highlight" | "label" | "required" | "helpText" | "isBetaFeature"
> & { htmlFor?: InputLabelProps["htmlFor"] } & GroupProps;

/** Pass `htmlFor` to render a real `<label>`; without it the text renders in a `div`, so it can sit inside another label. */
function FormFieldLabel({
  highlight,
  label,
  required,
  helpText,
  isBetaFeature,
  htmlFor,
  ...groupProps
}: FormFieldLabelProps) {
  return (
    <Group
      align="flex-start"
      className="FormFieldLabel"
      gap={4}
      mb="xs"
      wrap="nowrap"
      {...groupProps}
    >
      <Group gap="sm" wrap="nowrap">
        {highlight && <OrangeCircle />}
        <InputLabel htmlFor={htmlFor} labelElement={htmlFor ? "label" : "div"}>
          {label}
          {!!label && required && (
            <Text aria-hidden="true" c="var(--input-asterisk-color)" span>
              *
            </Text>
          )}
        </InputLabel>
      </Group>
      {(isBetaFeature || helpText) && (
        <Group flex="none" gap={4} mt={2} wrap="nowrap">
          {isBetaFeature && <IconBetaFeature />}
          {helpText && (
            <IconHelpText helpText={helpText} popoverPosition="top" size={16} />
          )}
        </Group>
      )}
    </Group>
  );
}

export { FormFieldLabel, FormFieldLabelProps };
